/// <amd-module name="Wit/WorkItemForm/Links/Controls/LinksZeroState" />
import * as React from "react";
import { FormatComponent } from "VSS/Platform/Components/Format/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { usePageContext } from "VSS/Platform/Layout";
import { Link as AnchorLink } from "VSSUI/Link";
import { VssIcon, VssIconType } from "VSSUI/VssIcon";
import { ZeroData } from "VSSUI/ZeroData";
import { useAssetLocation, useWorkItem } from "Wit/WorkItemForm/Common/Hooks";
import * as Resources from "Wit/WorkItemForm/Resources";
import {
    branchLinkTypeFilter,
    commitLinkTypeFilter,
    LinkTypeFilter,
    newLinksFilter,
    parentLinkTypeFilter,
    pullRequestLinkTypeFilter,
    githubCommitLinkTypeFilter,
    githubPullRequestLinkTypeFilter
} from "Wit/Common/Utils/WorkItemUtils/LinkTypeFilter";
import { AddLinkButton } from "./AddLinkButton";

export interface ILinksZeroStateProps {
    openAddLinksDialog: (filter: LinkTypeFilter) => void;
    linkTypeFilter: LinkTypeFilter;
    zeroDataExperience?: string;
    type?: string;
}

export function LinksZeroState({ zeroDataExperience, openAddLinksDialog, linkTypeFilter, type }: ILinksZeroStateProps): JSX.Element | null {
    const workItem = useWorkItem();

    if (zeroDataExperience && workItem.isDeleted()) {
        return null;
    }

    switch (zeroDataExperience) {
        case "Development":
            if (type === "zeroGitHubLinks") {
                return <DevelopmentGitHubZeroState openAddLinksDialog={openAddLinksDialog} />;
            }
            return <DevelopmentZeroState openAddLinksDialog={openAddLinksDialog} />;
        case "RelatedWork":
            return <RelatedWorkZeroState openAddLinksDialog={openAddLinksDialog} />;
        default:
            const renderAddLinkButton = (workItemIsDeleted: boolean): JSX.Element => {
                if (workItemIsDeleted) {
                    return <></>;
                } else {
                    return (
                        <AddLinkButton
                            primary
                            onLinkToExistingItem={() => openAddLinksDialog(linkTypeFilter)}
                            onLinkToNewItem={() => openAddLinksDialog(newLinksFilter)}
                            linkTypeFilter={linkTypeFilter}
                            isNewItemLinkDisabled={workItem.isNew()}
                        />
                    );
                }
            };

            return (
                <ZeroData
                    iconProps={{ iconName: "Link" }}
                    imageAltText={Resources.LinksZeroData}
                    secondaryText={Resources.LinksZeroData}
                    renderAction={() => renderAddLinkButton(workItem.isDeleted())}
                />
            );
    }
}

interface IDevelopmentZeroStateProps {
    openAddLinksDialog: (filter: LinkTypeFilter) => void;
}

function DevelopmentZeroState({ openAddLinksDialog }: IDevelopmentZeroStateProps): JSX.Element {
    const pageContext = usePageContext();
    const workItem = useWorkItem();
    const asset = useAssetLocation("ms.vss-code-web/common-content/Nav-Code.png");
    return (
        <div className="links-control-zero-state flex-row flex-center">
            <img aria-hidden={true} className="development-zero-state-image" src={asset} />
            <FormatComponent format={Resources.AddLinkToExistingDevelopmentItem}>
                {[
                    <AnchorLink key="commit-link-dialog" onClick={() => openAddLinksDialog(commitLinkTypeFilter)}>
                        {Resources.AddLinkToExistingCommit}
                    </AnchorLink>,
                    <AnchorLink key="pull-request-link-dialog" onClick={() => openAddLinksDialog(pullRequestLinkTypeFilter)}>
                        {Resources.AddLinkToExistingPullRequest}
                    </AnchorLink>,
                    <AnchorLink key="branch-link-dialog" onClick={() => openAddLinksDialog(branchLinkTypeFilter)}>
                        {Resources.AddLinkToExistingBranch}
                    </AnchorLink>,
                    <AnchorLink key="new-branch-link-dialog" onClick={() => openNewBranchDialog(pageContext, workItem.id)}>
                        {Resources.AddLinkToNewBranch}
                    </AnchorLink>
                ]}
            </FormatComponent>
        </div>
    );
}

function openNewBranchDialog(pageContext: IVssPageContext, workItemId: number) {
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    contributionService.executeCommandEx(
        "ms.vss-code-web.tags-version-dialog-actions-command",
        {
            dependencies: ["ms.vss-code-web.version-dialogs-content", "ms.vss-code-web.versioncontrol-viewmodel-data-provider"],
            serviceName: "IVersionDialogActionsService",
            methodName: "createBranch"
        },
        {
            initialWorkItemIds: [workItemId],
            allowChangingRepository: true
        }
    );
}

interface IRelatedWorkZeroStateProps {
    openAddLinksDialog: (filter: LinkTypeFilter) => void;
}

function RelatedWorkZeroState({ openAddLinksDialog }: IRelatedWorkZeroStateProps): JSX.Element {
    return (
        <div className="links-control-zero-state">
            <AnchorLink onClick={() => openAddLinksDialog(parentLinkTypeFilter)}>{Resources.AddLinkToExistingParent}</AnchorLink>
        </div>
    );
}

function DevelopmentGitHubZeroState({ openAddLinksDialog }: IDevelopmentZeroStateProps): JSX.Element {
    return (
        <div className="links-control-zero-state github flex-row flex-center">
            <VssIcon className="zero-data-icon" iconName="GitHubLogo" iconType={VssIconType.fabric} />
            <FormatComponent format={Resources.GitHubZeroDataText}>
                {[
                    <AnchorLink key="github-commit-link-dialog" onClick={() => openAddLinksDialog(githubCommitLinkTypeFilter)}>
                        {Resources.GitHubZeroDataCommitLinkTypeText}
                    </AnchorLink>,
                    <AnchorLink key="github-pull-request-link-dialog" onClick={() => openAddLinksDialog(githubPullRequestLinkTypeFilter)}>
                        {Resources.GitHubZeroDataPullRequestTypeText}
                    </AnchorLink>,
                    <AnchorLink key="github-learn-more-link" href={Resources.GitHubAzureBoardsDocUrl} target="_blank">
                        {Resources.GitHubZeroDataTextLearnMore}
                    </AnchorLink>
                ]}
            </FormatComponent>
        </div>
    );
}
